import {Color} from '@benchling/nucleus-ui';
import {css, StyleSheet} from 'aphrodite';
import classNames from 'classnames';
import React from 'react';

import ModelTypeConstants from 'constants/ModelTypeConstants';
import {TestOrderStatus} from 'graphql-client/__generated/graphqlGlobalTypes';
import {Filter, FilterKey, Operator} from 'search/SearchConstants';
import {switchToStudyItemsTab} from 'studies/workspace/StudySearchHelpers';
import {FONT_PROXIMA_13, FONT_WEIGHT_PROXIMA_REGULAR} from 'styles/FontConstants';
import {ChipContent, ChipSize, ChipToggle} from 'ui-library/chips/Chip';
import BenchlingIcon, {BenchlingIconName} from 'ui-library/icons/BenchlingIcon';
import {BasicEvent} from 'util/Types';

interface SampleDefinitionChipProps {
  studyId: string;
  sampleDefinitionId: string | null;
}

export interface SampleReceivingChipProps extends SampleDefinitionChipProps {
  receivedCount: number;
}
export interface SampleDiscrepancyChipProps extends SampleDefinitionChipProps {
  discrepancyCount: number;
}
export interface SampleTargetedChipProps extends SampleDefinitionChipProps {
  stillTargetedCount: number;
  receivedCount: number;
}
export interface SampleTestOrderChipProps extends SampleDefinitionChipProps {
  assayId: string;
  receivedCount: number;
  resolvedCount: number;
  totalCount: number;
}

const sampleFilter: Filter = {
  key: FilterKey.TYPES,
  operator: Operator.IS_ONE_OF,
  value: [ModelTypeConstants.BASIC_FOLDER_ITEM],
};
const testOrderFilter: Filter = {
  key: FilterKey.TYPES,
  operator: Operator.IS_ONE_OF,
  value: [ModelTypeConstants.TEST_ORDER],
};

function makeTestOrderStatusFilter(isResolved: boolean): Filter {
  return {
    key: FilterKey.TEST_STATUS,
    operator: Operator.IS_ONE_OF,
    value: isResolved
      ? [TestOrderStatus.COMPLETED, TestOrderStatus.CANCELLED, TestOrderStatus.INVALID]
      : [TestOrderStatus.BLOCKED, TestOrderStatus.IN_PROGRESS, TestOrderStatus.PENDING],
  };
}

function makeSampleDefinitionFilter(sampleDefinitionId: string | null): Filter {
  return sampleDefinitionId
    ? {
        key: FilterKey.SAMPLE_DEFINITION,
        operator: Operator.IS_ONE_OF,
        value: [sampleDefinitionId],
      }
    : {
        key: FilterKey.SAMPLE_DEFINITION,
        operator: Operator.IS_EMPTY,
        value: null,
      };
}

function makeAssayFilter(assayId: string): Filter {
  return {
    key: FilterKey.ASSAY_TEMPLATE,
    operator: Operator.IS_ONE_OF,
    value: [assayId],
  };
}

function makeSampleChipClick(studyId: string, filters: Array<Filter>): (e: BasicEvent) => void {
  return (e: BasicEvent): void => {
    switchToStudyItemsTab(studyId, filters);
  };
}

export function DiscrepancyChip(props: SampleDiscrepancyChipProps): JSX.Element | null {
  if (props.discrepancyCount > 0) {
    return makeSampleChip(`${props.discrepancyCount} discrepancies`, SampleChipType.ERROR, null);
  }
  return null;
}

export function ReceivedChip(props: SampleReceivingChipProps): JSX.Element | null {
  if (props.receivedCount > 0) {
    const onClick = makeSampleChipClick(props.studyId, [
      sampleFilter,
      makeSampleDefinitionFilter(props.sampleDefinitionId),
    ]);
    return makeSampleChip(`${props.receivedCount} received`, SampleChipType.DONE, onClick);
  }
  return null;
}

export function TargetedChip(props: SampleTargetedChipProps): JSX.Element | null {
  if (props.stillTargetedCount > 0) {
    const text =
      props.receivedCount > 0
        ? `${props.stillTargetedCount} still targeted`
        : `${props.stillTargetedCount} targeted`;
    return makeSampleChip(text, SampleChipType.TARGETED, null);
  }
  return null;
}

export function TestOrderChips(props: SampleTestOrderChipProps): JSX.Element {
  if (props.receivedCount === 0) {
    return <div className={css(styles.noSamplesReceivedText)}>No samples received yet</div>;
  }
  if (props.resolvedCount === 0 && props.totalCount === 0) {
    return <div className={css(styles.noSamplesReceivedText)}>No tests ordered yet</div>;
  }

  const onResolvedTestOrdersClick = makeSampleChipClick(props.studyId, [
    testOrderFilter,
    makeSampleDefinitionFilter(props.sampleDefinitionId),
    makeAssayFilter(props.assayId),
    makeTestOrderStatusFilter(true),
  ]);
  const onOrderedTestOrdersClick = makeSampleChipClick(props.studyId, [
    testOrderFilter,
    makeSampleDefinitionFilter(props.sampleDefinitionId),
    makeAssayFilter(props.assayId),
    makeTestOrderStatusFilter(false),
  ]);

  const resolvedChip =
    props.resolvedCount > 0
      ? makeSampleChip(`${props.resolvedCount} resolved`, SampleChipType.RESOLVED, onResolvedTestOrdersClick)
      : null;
  const orderedChip =
    props.resolvedCount < props.totalCount
      ? makeSampleChip(
          `${props.totalCount - props.resolvedCount} ordered`,
          SampleChipType.ORDERED,
          onOrderedTestOrdersClick
        )
      : null;

  return (
    <div className={css(styles.testOrderChipsContainer)}>
      <span>{resolvedChip}</span>
      <span>{orderedChip}</span>
    </div>
  );
}

/* exported for testing */
export enum SampleChipType {
  DONE = 'done',
  ERROR = 'error',
  TARGETED = 'targeted',
  ORDERED = 'ordered',
  RESOLVED = 'resolved',
}

/* exported for testing */
export function makeSampleChip(
  text: string,
  sampleChipType: SampleChipType,
  onClick: ((e: BasicEvent) => void) | null
): React.JSX.Element {
  const sampleChipTypeToIcon: Record<SampleChipType, BenchlingIconName> = {
    [SampleChipType.DONE]: 'done',
    [SampleChipType.ERROR]: 'exclamation',
    [SampleChipType.TARGETED]: 'calendar',
    [SampleChipType.ORDERED]: 'circle-outline',
    [SampleChipType.RESOLVED]: 'check',
  };

  const sampleChipTypeToIconClassNames: Record<SampleChipType, Array<string>> = {
    [SampleChipType.DONE]: [css(styles.chipIconDone)],
    [SampleChipType.ERROR]: [css(styles.chipIconError)],
    [SampleChipType.TARGETED]: [css(styles.chipIconTargeted)],
    [SampleChipType.ORDERED]: [css(styles.chipIconOrdered)],
    [SampleChipType.RESOLVED]: [css(styles.chipIconResolved)],
  };

  const icon = sampleChipTypeToIcon[sampleChipType];
  const iconClassNames = sampleChipTypeToIconClassNames[sampleChipType];

  return (
    <ChipToggle
      className={classNames(css(styles.chip), onClick == null ? css(styles.chipWithoutBorder) : null)}
      size={ChipSize.TABLE_CELL}
      onClick={onClick}
      isLink={onClick != null}
    >
      <div className={css(styles.chipContent)}>
        {icon && (
          <BenchlingIcon
            icon={icon}
            className={classNames(css(styles.chipIcon), ...(iconClassNames ?? [css(styles.chipIconDefault)]))}
          />
        )}
        <ChipContent text={text} />
      </div>
    </ChipToggle>
  );
}

/* exported for testing */
export const styles = StyleSheet.create({
  chipContent: {
    whiteSpace: 'nowrap',
    overflow: 'hidden',
    display: 'flex',
    flexDirection: 'row',
    alignItems: 'center',
    ...FONT_PROXIMA_13,
    fontWeight: FONT_WEIGHT_PROXIMA_REGULAR,
    color: Color.GRAY_700,
  },
  chip: {
    backgroundColor: Color.WHITE,
    paddingTop: 2,
  },
  chipWithoutBorder: {
    border: 'none',
  },
  chipIcon: {
    marginRight: 4,
  },
  chipIconDefault: {
    color: Color.GRAY_600,
  },
  chipIconDone: {
    color: Color.GREEN_500,
  },
  chipIconError: {
    color: Color.RED_500,
  },
  chipIconTargeted: {
    color: Color.GRAY_600,
    marginTop: -1,
  },
  chipIconOrdered: {
    color: Color.GRAY_400,
  },
  chipIconResolved: {
    color: Color.BLUE_500,
  },
  noSamplesReceivedText: {
    ...FONT_PROXIMA_13,
    fontWeight: FONT_WEIGHT_PROXIMA_REGULAR,
    color: Color.GRAY_600,
    textAlign: 'right',
  },
  testOrderChipsContainer: {
    display: 'flex',
    flexDirection: 'row',
    alignItems: 'center',
    flexWrap: 'wrap',
    rowGap: 6,
  },
});
